"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Button,
  useTheme,
  alpha,
} from "@mui/material"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Subscriptions as SubscriptionsIcon,
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { motion } from "framer-motion"
import { useAuth } from "../../contexts/AuthContext"
import { getOrganizationStats } from "../../services/organization.service"
import { getOrganization } from "../../services/organization.service"
import { getStats, getTrendData, getSubscriptionDistribution } from "../../services/dashboard.service"
import PageHeader from "../../components/common/PageHeader"
import StatCard from "../../components/dashboard/StatCard"
import RecentActivity from "../../components/dashboard/RecentActivity"
import QuickActions from "../../components/dashboard/QuickActions"

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user, selectedOrganization, selectOrganization } = useAuth()
  const theme = useTheme()
  const [dashboardData, setDashboardData] = useState({
    customers: { count: 0, trend: 0 },
    subscriptions: { count: 0, trend: 0 },
    credits: { count: 0, trend: 0 },
  })
  const [organizationLoaded, setOrganizationLoaded] = useState(false)
  const [trendData, setTrendData] = useState([])
  const [pieData, setPieData] = useState([])

  // Check if user is superadmin
  const isSuperAdmin = user && user.role === "superadmin"

  // Set user's organization if not superadmin
  useEffect(() => {
    const setUserOrganization = async () => {
      if (!user) return

      // If not superadmin and no organization is selected yet
      if (!isSuperAdmin && !selectedOrganization && !organizationLoaded) {
        try {
          console.log("user", user)
          // Use organization_id directly from user object
          if (user.organizationId) {
            console.log("Using organization_id from user object:", user.organizationId)

            // Fetch organization details if needed
            try {
              const orgResponse = await getOrganization(user.organizationId)
              if (orgResponse && orgResponse.data) {
                selectOrganization(orgResponse.data)
              } else {
                // If we can't get full details, at least set the ID
                selectOrganization({ _id: user.organizationId, name: "Your Organization" })
              }
            } catch (orgError) {
              console.error("Error fetching organization details:", orgError)
              // Still set the organization ID even if details fetch fails
              selectOrganization({ _id: user.organizationId, name: "Your Organization" })
            }
          } else {
            console.error("No organizationId found in user object")
            setError("No organization assigned to your account. Please contact an administrator.")
          }
        } catch (err) {
          console.error("Error setting user organization:", err)
          setError("Failed to load your organization. Please try again later.")
        } finally {
          setOrganizationLoaded(true)
        }
      } else if (isSuperAdmin && !selectedOrganization) {
        // For superadmins without a selected organization
        setOrganizationLoaded(true)
      } else if (selectedOrganization) {
        // If organization is already selected
        setOrganizationLoaded(true)
      }
    }

    setUserOrganization()
  }, [user, isSuperAdmin, selectedOrganization, organizationLoaded, selectOrganization])

  const fetchDashboardData = async () => {
    setLoading(true)
    setError(null)

    try {
      let organizationId

      // For non-superadmins, always use the organization_id from the user object
      if (!isSuperAdmin && user && user.organization_id) {
        organizationId = user.organization_id
      }
      // For superadmins, use the selected organization if available
      else if (selectedOrganization && selectedOrganization._id) {
        organizationId = selectedOrganization._id
      }
      // If no organization is available
      else {
        throw new Error("No organization selected")
      }

      console.log("Fetching dashboard data for organization:", organizationId)

      // Get organization stats from the API
      const statsResponse = await getOrganizationStats(organizationId)
      console.log("Stats response:", statsResponse)

      // Use the stats data we already have
      if (statsResponse && statsResponse.data) {
        setDashboardData({
          customers: { count: statsResponse.data.customerCount || 0, trend: 0 },
          subscriptions: { count: statsResponse.data.activeSubscriptions || 0, trend: 0 },
          credits: { count: 0, trend: 0 },
        })
      }

      // Also fetch the stats for the charts
      const statsForCharts = await getStats(organizationId)

      if (statsForCharts && statsForCharts.success && statsForCharts.data) {
        const data = statsForCharts.data

        // Set the stats with default values if properties are missing
        setStats({
          userCount: data.userCount || 0,
          customerCount: data.customerCount || 0,
          activeSubscriptions: data.activeSubscriptions || 0,
          availableCredits: data.availableCredits || 0, // Add availableCredits to stats
          userTrend: data.userTrend || 0, // Add trends to stats
          customerTrend: data.customerTrend || 0,
          subscriptionTrend: data.subscriptionTrend || 0,
          creditTrend: data.creditTrend || 0
        })

        // Create chart data separately to avoid the undefined error
        setChartData([
          {
            name: "Users",
            count: data.userCount || 0,
          },
          {
            name: "Customers",
            count: data.customerCount || 0,
          },
          {
            name: "Subscriptions",
            count: data.activeSubscriptions || 0,
          },
        ])
      }

      // Fallback mock data for trends (since API endpoint is 404)
      const mockTrendData = [
        { name: "Jan", users: 4000, customers: 2400 },
        { name: "Feb", users: 3000, customers: 1398 },
        { name: "Mar", users: 2000, customers: 9800 },
        { name: "Apr", users: 2780, customers: 3908 },
        { name: "May", users: 1890, customers: 4800 },
        { name: "Jun", users: 2390, customers: 3800 },
        { name: "Jul", users: 3490, customers: 4300 },
      ]

      // Fallback mock data for subscription distribution (since API endpoint is 404)
      const mockPieData = [
        { name: "Basic", value: 400 },
        { name: "Standard", value: 300 },
        { name: "Premium", value: 200 },
        { name: "Enterprise", value: 100 },
      ]

      // Try to fetch real trend data first
      try {
        const trendResponse = await getTrendData("month")
        if (trendResponse && trendResponse.success && trendResponse.data) {
          setTrendData(trendResponse.data)
        } else {
          // Use mock data if API response is not in expected format
          console.log("Using mock trend data")
          setTrendData(mockTrendData)
        }
      } catch (trendError) {
        console.error("Error fetching trend data:", trendError)
        // Use mock data when API fails (404)
        console.log("Using mock trend data after error")
        setTrendData(mockTrendData)
      }

      // Try to fetch real subscription distribution data first
      try {
        const distributionResponse = await getSubscriptionDistribution()
        if (distributionResponse && distributionResponse.success && distributionResponse.data) {
          setPieData(distributionResponse.data)
        } else {
          // Use mock data if API response is not in expected format
          console.log("Using mock subscription distribution data")
          setPieData(mockPieData)
        }
      } catch (distributionError) {
        console.error("Error fetching subscription distribution:", distributionError)
        // Use mock data when API fails (404)
        console.log("Using mock subscription distribution data after error")
        setPieData(mockPieData)
      }
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
      setError(error.message || "Failed to load dashboard data")
      // Set default data to prevent continuous loading
      setDashboardData({
        customers: { count: 0, trend: 0 },
        subscriptions: { count: 0, trend: 0 },
        credits: { count: 0, trend: 0 },
      })

      // Set default stats for charts
      setStats({
        userCount: 0,
        customerCount: 0,
        activeSubscriptions: 0,
        availableCredits: 0,
        userTrend: 0,
        customerTrend: 0,
        subscriptionTrend: 0,
        creditTrend: 0
      })

      setChartData([
        { name: "Users", count: 0 },
        { name: "Customers", count: 0 },
        { name: "Subscriptions", count: 0 },
      ])
      
      // Set mock data for trend and pie data even when there's an error
      setTrendData([
        { name: "Jan", users: 4000, customers: 2400 },
        { name: "Feb", users: 3000, customers: 1398 },
        { name: "Mar", users: 2000, customers: 9800 },
        { name: "Apr", users: 2780, customers: 3908 },
        { name: "May", users: 1890, customers: 4800 },
        { name: "Jun", users: 2390, customers: 3800 },
        { name: "Jul", users: 3490, customers: 4300 },
      ])
      
      setPieData([
        { name: "Basic", value: 400 },
        { name: "Standard", value: 300 },
        { name: "Premium", value: 200 },
        { name: "Enterprise", value: 100 },
      ])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // For non-superadmins, we can fetch data as soon as we have the user
    if (!isSuperAdmin && user && user.organization_id) {
      fetchDashboardData()
    }
    // For superadmins, we need to wait for organization selection
    else if (organizationLoaded && selectedOrganization && selectedOrganization._id) {
      fetchDashboardData()
    }
    // If organization is loaded but none is selected (for superadmins)
    else if (organizationLoaded && isSuperAdmin && (!selectedOrganization || !selectedOrganization._id)) {
      setLoading(false)
    }
  }, [user, selectedOrganization, organizationLoaded, isSuperAdmin])

  const handleRefresh = () => {
    fetchDashboardData()
  }

  // Colors for pie chart
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" flexDirection="column" gap={2}>
          <Typography color="error" variant="h6">
            Error: {error}
          </Typography>
          <Button variant="contained" onClick={handleRefresh}>
            Retry
          </Button>
        </Box>
      </Container>
    )
  }

  // For superadmins without a selected organization
  if (isSuperAdmin && (!selectedOrganization || !selectedOrganization._id)) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" flexDirection="column" gap={2}>
          <Typography variant="h6">Please select an organization to view the dashboard</Typography>
        </Box>
      </Container>
    )
  }

  // Make sure we have stats before rendering
  if (!stats) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Typography>No data available</Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your business metrics"
        action={
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Refresh
          </Button>
        }
      />

      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3}>
          {/* Stats Cards */}
          <Grid item xs={12} md={6} lg={3}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <StatCard
                title="Total Users"
                value={stats.userCount}
                icon={<PeopleIcon />}
                color={theme.palette.primary.main}
                trend={stats.userTrend}
                trendLabel="vs last month"
                linkTo="/users"
              />
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <StatCard
                title="Total Customers"
                value={stats.customerCount}
                icon={<BusinessIcon />}
                color={theme.palette.secondary.main}
                trend={stats.customerTrend}
                trendLabel="vs last month"
                linkTo="/customers"
              />
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <StatCard
                title="Active Subscriptions"
                value={stats.activeSubscriptions}
                icon={<SubscriptionsIcon />}
                color={theme.palette.success.main}
                trend={stats.subscriptionTrend}
                trendLabel="vs last month"
                linkTo="/subscriptions"
              />
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <StatCard
                title="Available Credits"
                value={stats.availableCredits}
                icon={<CreditCardIcon />}
                color={theme.palette.warning.main}
                trend={stats.creditTrend}
                trendLabel="vs last month"
                linkTo="/credits"
              />
            </motion.div>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} lg={8}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Growth Trends
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={trendData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.05)} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke={theme.palette.primary.main}
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                    <Line type="monotone" dataKey="customers" stroke={theme.palette.secondary.main} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </motion.div>
          </Grid>

          {/* Quick Actions and Recent Activity */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={3} direction="column">
              <Grid item>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <QuickActions />
                </motion.div>
              </Grid>

              <Grid item>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <RecentActivity />
                </motion.div>
              </Grid>
            </Grid>
          </Grid>

          {/* Additional Charts */}
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Key Metrics
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha("#000", 0.05)} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Subscription Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </Container>
  )
}

export default Dashboard

